"use client";

import * as React from "react";
import { ArrowLeft, Download, Loader2, ZoomIn, ZoomOut, RotateCcw, PanelLeft, Search, ChevronUp, ChevronDown, X } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import annotationLayerCss from "react-pdf/dist/Page/AnnotationLayer.css?inline";
import textLayerCss from "react-pdf/dist/Page/TextLayer.css?inline";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Side-effect CSS imports land in the <head> which doesn't reach the shadow DOM.
// Using ?inline captures the CSS as strings so we can inject them via a <style> tag.
const pdfLayerCss = annotationLayerCss + textLayerCss;

const pdfSearchCss = `
::highlight(pdf-search-highlight) {
  background-color: rgba(255, 213, 0, 0.4);
}
::highlight(pdf-search-highlight-active) {
  background-color: rgba(255, 150, 0, 0.6);
}
mark.pdf-search-mark {
  background-color: rgba(255, 213, 0, 0.4);
  color: inherit;
}
mark.pdf-search-mark-active {
  background-color: rgba(255, 150, 0, 0.6);
  color: inherit;
}
`;

import { CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { WidgetWindow } from "@/components/ui/widget-window/widget-window";
import { createDimensionsStorage, type DimensionsStorage } from "@/lib/window-storage";
import { usePersonation } from "@/lib/personation-context";
import { TOP_LAYER_Z } from "@/lib/z-index";
import { usePdfViewer } from "./pdf-context";
import { useFetchPdfBlob } from "./use-fetch-pdf-blob";
import { usePdfSearch } from "./use-pdf-search";
import { useWidgetOverlayOptional, useDragHandle } from "@/components/ui/widget/widget-overlay-context";

// ---------------------------------------------------------------------------
// DownloadButton
// ---------------------------------------------------------------------------

/** Props accepted by the DownloadButton component. */
type DownloadButtonProps = {
  blobUrl: string | null;
  title: string;
};

/**
 * Button that downloads the current PDF via a temporary anchor element.
 *
 * Disabled when no blob URL is available yet (e.g. still loading).
 */
function DownloadButton({ blobUrl, title }: DownloadButtonProps) {
  const handleDownload = React.useCallback(() => {
    if (!blobUrl) return;

    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    const safeTitle = (title || "document").replace(/[^a-zA-Z0-9_\-\s]/g, "_");
    anchor.download = `${safeTitle}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }, [blobUrl, title]);

  return (
    <Button
      size="icon"
      variant="ghost"
      data-testid="pdf-download-button"
      aria-label="Download PDF"
      disabled={!blobUrl}
      onClick={handleDownload}
    >
      <Download className="h-4 w-4" />
    </Button>
  );
}

// ---------------------------------------------------------------------------
// PdfSearchBar
// ---------------------------------------------------------------------------

/** Props accepted by the PdfSearchBar component. */
type PdfSearchBarProps = {
  query: string;
  setQuery: (q: string) => void;
  activeIndex: number;
  totalMatches: number;
  onNext: () => void;
  onPrevious: () => void;
  onClose: () => void;
  placeholder: string;
};

/**
 * Inline search bar for the PDF viewer.
 *
 * Renders a text input with navigation controls (previous/next match)
 * and a match counter. Supports keyboard shortcuts: Enter to go to
 * the next match, Shift+Enter for the previous match, and Escape to
 * close the search bar.
 */
function PdfSearchBar({
  query,
  setQuery,
  activeIndex,
  totalMatches,
  onNext,
  onPrevious,
  onClose,
  placeholder,
}: PdfSearchBarProps) {
  /** Handles keyboard shortcuts within the search input. */
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && e.shiftKey) {
        e.preventDefault();
        onPrevious();
      } else if (e.key === "Enter") {
        e.preventDefault();
        onNext();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    },
    [onNext, onPrevious, onClose],
  );

  return (
    <div
      className="absolute top-2 right-2 z-20 flex items-center gap-1.5 px-3 py-1.5 bg-background border rounded-lg text-xs"
      style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)" }}
      data-testid="pdf-search-bar"
    >
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        className="w-44 bg-transparent border-none outline-none text-sm placeholder:text-muted-foreground"
        placeholder={placeholder}
        data-testid="pdf-search-input"
        autoFocus
      />
      {query && (
        <span
          className="text-muted-foreground whitespace-nowrap"
          data-testid="pdf-search-count"
        >
          {totalMatches > 0 ? `${activeIndex + 1} / ${totalMatches}` : "0 results"}
        </span>
      )}
      <Button
        size="icon"
        variant="ghost"
        className="h-6 w-6"
        onClick={onPrevious}
        disabled={totalMatches === 0}
        aria-label="Previous match"
        data-testid="pdf-search-prev"
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="h-6 w-6"
        onClick={onNext}
        disabled={totalMatches === 0}
        aria-label="Next match"
        data-testid="pdf-search-next"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="h-6 w-6"
        onClick={onClose}
        aria-label="Close search"
        data-testid="pdf-search-close"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PdfBody
// ---------------------------------------------------------------------------

const ZOOM_STEP = 0.15;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3.0;
const INITIAL_SCALE = 0.99;
const DEFAULT_THUMBNAIL_WIDTH = 160;
// handle(4) + container p-3(24) + button p-1(8) + ring clearance(4) = 40
const SIDEBAR_PADDING = 40;
const MIN_SIDEBAR_WIDTH = 100 + SIDEBAR_PADDING;
const MAX_SIDEBAR_WIDTH = 350 + SIDEBAR_PADDING;
const DEFAULT_SIDEBAR_WIDTH = DEFAULT_THUMBNAIL_WIDTH + SIDEBAR_PADDING;
const SIDEBAR_BREAKPOINT = DEFAULT_SIDEBAR_WIDTH + 300;
// Deep-link scroll: retries while waiting for a page's real layout height.
const MAX_SCROLL_ATTEMPTS = 60;
// Deep-link scroll: bounded re-corrections in response to scale changes
// (the auto-fit re-layout), so a later manual zoom isn't chased forever.
const MAX_SCROLL_CORRECTIONS = 3;

/** Props accepted by the PdfThumbnailSidebar component. */
type PdfThumbnailSidebarProps = {
  blobUrl: string;
  totalPages: number;
  currentPage: number;
  overlay: boolean;
  onPageClick: (pageNumber: number) => void;
};

/**
 * Sidebar panel showing small thumbnail previews of each PDF page.
 *
 * Clicking a thumbnail scrolls the main viewer to that page. The
 * current page thumbnail is highlighted with a ring. A drag handle
 * on the right edge allows resizing the sidebar width.
 */
function PdfThumbnailSidebar({
  blobUrl,
  totalPages,
  currentPage,
  overlay,
  onPageClick,
}: PdfThumbnailSidebarProps) {
  const sidebarRef = React.useRef<HTMLDivElement>(null);
  const thumbRefs = React.useRef<Map<number, HTMLButtonElement>>(new Map());
  const [sidebarWidth, setSidebarWidth] = React.useState(DEFAULT_SIDEBAR_WIDTH);

  const thumbnailWidth = sidebarWidth - SIDEBAR_PADDING;

  // Auto-scroll the sidebar so the active thumbnail stays visible
  React.useEffect(() => {
    const el = thumbRefs.current.get(currentPage);
    if (el) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [currentPage]);

  // Drag-to-resize handler
  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = sidebarWidth;

      const onMouseMove = (ev: MouseEvent) => {
        const delta = ev.clientX - startX;
        const newWidth = Math.min(
          MAX_SIDEBAR_WIDTH,
          Math.max(MIN_SIDEBAR_WIDTH, startWidth + delta),
        );
        setSidebarWidth(newWidth);
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [sidebarWidth],
  );

  return (
    <div
      ref={sidebarRef}
      className={`flex flex-row shrink-0 ${
        overlay
          ? "absolute left-0 top-0 bottom-0 z-10 bg-background shadow-lg"
          : "bg-muted/20"
      }`}
      style={{ width: sidebarWidth }}
      data-testid="pdf-thumbnail-sidebar"
    >
      <div className="flex-1 flex flex-col gap-2 p-3 overflow-y-auto overflow-x-hidden">
        <Document file={blobUrl} loading={null} error={null}>
          {Array.from({ length: totalPages }, (_, index) => {
            const pageNum = index + 1;
            const isActive = pageNum === currentPage;
            return (
              <button
                key={`thumb_${pageNum}`}
                ref={(el) => {
                  if (el) thumbRefs.current.set(pageNum, el);
                  else thumbRefs.current.delete(pageNum);
                }}
                type="button"
                className={`flex flex-col items-center gap-1 p-1 rounded cursor-pointer transition-colors ${
                  isActive
                    ? "ring-2 ring-primary bg-primary/10"
                    : "hover:bg-muted/50"
                }`}
                data-testid={`pdf-thumbnail-${pageNum}`}
                aria-label={`Go to page ${pageNum}`}
                onClick={() => onPageClick(pageNum)}
              >
                <div style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)" }}>
                  <Page
                    pageNumber={pageNum}
                    width={thumbnailWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </div>
                <span className="text-[10px] text-muted-foreground">{pageNum}</span>
              </button>
            );
          })}
        </Document>
      </div>
      {/* Resize handle */}
      <div
        className="w-1 cursor-col-resize hover:bg-primary/30 active:bg-primary/50 transition-colors shrink-0"
        data-testid="pdf-sidebar-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        onMouseDown={handleMouseDown}
      />
    </div>
  );
}

/** Props accepted by the PdfToolbar component. */
type PdfToolbarProps = {
  currentPage: number;
  totalPages: number;
  scale: number;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
  onSearch: () => void;
};

/**
 * Toolbar with zoom controls and page indicator for the PDF viewer.
 */
function PdfToolbar({
  currentPage,
  totalPages,
  scale,
  sidebarOpen,
  onToggleSidebar,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onSearch,
}: PdfToolbarProps) {
  return (
    <div
      className="flex items-center justify-between px-3 py-1.5 border-b border-border/70 bg-background/90 text-[11px] text-muted-foreground/85"
      data-testid="pdf-toolbar"
    >
      <div className="flex items-center gap-1">
        <Button
          size="icon"
          variant={sidebarOpen ? "secondary" : "ghost"}
          className="h-6 w-6 text-muted-foreground/75 hover:text-foreground"
          data-testid="pdf-sidebar-toggle"
          aria-label={sidebarOpen ? "Hide page thumbnails" : "Show page thumbnails"}
          disabled={totalPages === 0}
          onClick={onToggleSidebar}
        >
          <PanelLeft className="h-3 w-3" />
        </Button>
        <span className="font-medium" data-testid="pdf-page-indicator">
          {totalPages > 0 ? `${currentPage} / ${totalPages}` : "–"}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6 text-muted-foreground/75 hover:text-foreground"
          data-testid="pdf-zoom-out"
          aria-label="Zoom out"
          disabled={scale <= MIN_ZOOM}
          onClick={onZoomOut}
        >
          <ZoomOut className="h-3 w-3" />
        </Button>
        <span className="w-12 text-center font-medium" data-testid="pdf-zoom-level">
          {Math.round(scale * 100)}%
        </span>
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6 text-muted-foreground/75 hover:text-foreground"
          data-testid="pdf-zoom-in"
          aria-label="Zoom in"
          disabled={scale >= MAX_ZOOM}
          onClick={onZoomIn}
        >
          <ZoomIn className="h-3 w-3" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6 text-muted-foreground/75 hover:text-foreground"
          data-testid="pdf-reset-zoom"
          aria-label="Reset zoom"
          onClick={onResetZoom}
        >
          <RotateCcw className="h-3 w-3" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6 text-muted-foreground/75 hover:text-foreground"
          data-testid="pdf-search-toggle"
          aria-label="Search in PDF"
          onClick={onSearch}
        >
          <Search className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

/** Props accepted by the PdfBody component. */
export type PdfBodyProps = {
  blobUrl: string | null;
  isLoading: boolean;
  error: string | null;
  /** Page to scroll to once the document has loaded and laid out. */
  initialPageNumber?: number;
};

/**
 * Renders the PDF content area using react-pdf.
 *
 * Displays a loading spinner while the PDF is being fetched, an error
 * message if fetching failed, or a scrollable multi-page PDF viewer
 * with zoom controls when ready.
 */
export function PdfBody({ blobUrl, isLoading, error, initialPageNumber }: PdfBodyProps) {
  const { labels } = usePersonation();
  const [totalPages, setTotalPages] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [scale, setScale] = React.useState(INITIAL_SCALE);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [sidebarOverlay, setSidebarOverlay] = React.useState(false);
  const [pageOriginalWidth, setPageOriginalWidth] = React.useState<number | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const pageRefs = React.useRef<Map<number, HTMLDivElement>>(new Map());

  const search = usePdfSearch({ containerRef, scale });

  // Open search bar on Ctrl+F / Cmd+F
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        search.open();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [search.open]);

  // Measure content area width to decide sidebar mode (side-by-side vs overlay)
  React.useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSidebarOverlay(entry.contentRect.width < SIDEBAR_BREAKPOINT);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const computeFitToWidthScale = React.useCallback(() => {
    const container = containerRef.current;
    if (!container || !pageOriginalWidth) return INITIAL_SCALE;
    const availableWidth = container.clientWidth - 2;
    const optimalScale = (availableWidth / pageOriginalWidth) * 0.85;
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, optimalScale));
  }, [pageOriginalWidth]);

  React.useEffect(() => {
    if (!pageOriginalWidth) return;
    const rafId = requestAnimationFrame(() => {
      const fitScale = computeFitToWidthScale();
      setScale(fitScale);
    });
    return () => cancelAnimationFrame(rafId);
  }, [pageOriginalWidth, computeFitToWidthScale]);

  // Track the current page via IntersectionObserver.
  // The observer callback only contains entries whose threshold changed,
  // so we accumulate ratios in a map and compare all pages each time.
  const visibilityMap = React.useRef<Map<number, number>>(new Map());

  React.useEffect(() => {
    if (totalPages === 0) return;

    const container = containerRef.current;
    if (!container) return;

    visibilityMap.current.clear();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const pageNum = Number(entry.target.getAttribute("data-page-number"));
          visibilityMap.current.set(pageNum, entry.intersectionRatio);
        }

        let mostVisiblePage = -1;
        let maxRatio = 0;
        for (const [pageNum, ratio] of visibilityMap.current) {
          if (ratio > maxRatio) {
            maxRatio = ratio;
            mostVisiblePage = pageNum;
          }
        }

        if (mostVisiblePage > 0) {
          setCurrentPage(mostVisiblePage);
        }
      },
      {
        root: container,
        threshold: [0, 0.25, 0.5, 0.75, 1.0],
      },
    );

    const rafId = requestAnimationFrame(() => {
      for (const [, el] of pageRefs.current) {
        observer.observe(el);
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [totalPages, scale]);

  // Scroll to the deep-linked page once its layout is real (has height).
  // Page refs for every page are created in bulk as soon as `totalPages`
  // arrives, well before react-pdf has laid out their content — so
  // `pageRefs.current.get(n)` existing is not a layout signal, only its
  // `offsetHeight` being non-zero is.
  //
  // The effect also depends on `scale` because the auto-fit effect above
  // re-layouts the document shortly after the first page loads, which
  // would otherwise undo an earlier scroll — so a scale change for the
  // SAME deep-link (same blobUrl + initialPageNumber) re-triggers the
  // scroll, bounded to a small number of corrections so it doesn't keep
  // chasing a later manual zoom forever. A different deep-link (new PDF
  // opened, or navigating to another page while the viewer stays open)
  // resets the tracking, since it's identified by blobUrl+page, not just
  // a one-shot flag — `PdfWindow`/`PdfBody` aren't remounted between
  // openPdf calls.
  const scrollStateRef = React.useRef<{ key: string; scale: number; corrections: number } | null>(null);

  React.useEffect(() => {
    if (!initialPageNumber || !blobUrl) return;
    if (totalPages === 0) return;

    const key = `${blobUrl}:${initialPageNumber}`;
    const state = scrollStateRef.current;
    if (state && state.key === key) {
      if (state.scale === scale) return;
      if (state.corrections >= MAX_SCROLL_CORRECTIONS) return;
    }

    let rafId: number;
    let attempts = 0;

    const tryScroll = () => {
      const el = pageRefs.current.get(initialPageNumber);
      if (el && el.offsetHeight > 0) {
        el.scrollIntoView({ behavior: "auto", block: "start" });
        scrollStateRef.current = {
          key,
          scale,
          corrections: state && state.key === key ? state.corrections + 1 : 0,
        };
        return;
      }
      attempts += 1;
      if (attempts >= MAX_SCROLL_ATTEMPTS) return;
      rafId = requestAnimationFrame(tryScroll);
    };

    rafId = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(rafId);
  }, [initialPageNumber, totalPages, scale, blobUrl]);

  const handleDocumentLoadSuccess = React.useCallback(
    ({ numPages }: { numPages: number }) => {
      setTotalPages(numPages);
      setCurrentPage(1);
      setPageOriginalWidth(null);
    },
    [],
  );

  const handleZoomIn = React.useCallback(() => {
    setScale((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const handleZoomOut = React.useCallback(() => {
    setScale((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  }, []);

  const handleResetZoom = React.useCallback(() => {
    setScale(computeFitToWidthScale());
  }, [computeFitToWidthScale]);

  const handleToggleSidebar = React.useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const handleThumbnailClick = React.useCallback((pageNumber: number) => {
    setCurrentPage(pageNumber);
    const el = pageRefs.current.get(pageNumber);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const setPageRef = React.useCallback(
    (pageNumber: number) => (el: HTMLDivElement | null) => {
      if (el) {
        pageRefs.current.set(pageNumber, el);
      } else {
        pageRefs.current.delete(pageNumber);
      }
    },
    [],
  );

  if (isLoading) {
    return (
      <CardContent
        className="flex flex-col flex-1 min-h-0 items-center justify-center"
        data-testid="pdf-loading"
      >
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">{labels.pdfViewer.loadingMessage}</p>
      </CardContent>
    );
  }

  if (error) {
    return (
      <CardContent
        className="flex flex-col flex-1 min-h-0 items-center justify-center"
        data-testid="pdf-error"
      >
        <p className="text-sm text-destructive">{labels.pdfViewer.loadingError}</p>
      </CardContent>
    );
  }

  if (blobUrl) {
    return (
      <>
        <style>{pdfLayerCss + pdfSearchCss}</style>
        <PdfToolbar
          currentPage={currentPage}
          totalPages={totalPages}
          scale={scale}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={handleToggleSidebar}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onResetZoom={handleResetZoom}
          onSearch={search.open}
        />
        <CardContent
          ref={contentRef}
          className="flex flex-row flex-1 min-h-0 p-0 overflow-hidden relative"
          data-testid="pdf-content"
        >
          {search.isOpen && (
            <PdfSearchBar
              query={search.query}
              setQuery={search.setQuery}
              activeIndex={search.activeIndex}
              totalMatches={search.matches.length}
              onNext={search.goToNext}
              onPrevious={search.goToPrevious}
              onClose={search.close}
              placeholder={labels.pdfViewer.searchPlaceholder}
            />
          )}
          {sidebarOpen && totalPages > 0 && (
            <PdfThumbnailSidebar
              blobUrl={blobUrl}
              totalPages={totalPages}
              currentPage={currentPage}
              overlay={sidebarOverlay}
              onPageClick={handleThumbnailClick}
            />
          )}
          <div
            ref={containerRef}
            className="flex-1 overflow-y-auto overflow-x-auto min-h-0 bg-muted/60"
            data-testid="pdf-scroll-container"
          >
            <Document
              file={blobUrl}
              onLoadSuccess={handleDocumentLoadSuccess}
              loading={
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              }
              error={
                <div className="flex items-center justify-center h-full">
                  <p className="text-sm text-destructive">
                    {labels.pdfViewer.loadingError}
                  </p>
                </div>
              }
            >
              {Array.from({ length: totalPages }, (_, index) => (
                <div
                  key={`page_${index + 1}`}
                  ref={setPageRef(index + 1)}
                  data-page-number={index + 1}
                  className="flex justify-center py-2"
                >
                  <div
                    style={{ boxShadow: "0 1px 6px rgba(0,0,0,0.14), 0 1px 2px rgba(0,0,0,0.08)" }}
                  >
                    <Page
                      pageNumber={index + 1}
                      scale={scale}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      onLoadSuccess={(page) => {
                        if (index === 0) {
                          setPageOriginalWidth(page.originalWidth);
                        }
                      }}
                    />
                  </div>
                </div>
              ))}
            </Document>
          </div>
        </CardContent>
      </>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Position helpers
// ---------------------------------------------------------------------------

const PDF_DEFAULT_WIDTH = 600;
const PDF_DEFAULT_HEIGHT = 700;
const VIEWPORT_MARGIN = 24;
const GAP = 12;

/**
 * Finds the chat widget frame element, searching through shadow roots
 * since the widget is rendered inside a Shadow DOM.
 */
function findChatFrame(): HTMLElement | null {
  const selector = '[data-dnd-id="widget-frame"]';

  // First try the light DOM (standard case)
  const direct = document.querySelector<HTMLElement>(selector);
  if (direct) return direct;

  // The widget uses ShadowHost which renders a <div> with an open shadow root.
  // Query only divs to narrow the search significantly.
  const candidates = document.querySelectorAll<HTMLElement>("div");
  for (const el of candidates) {
    if (el.shadowRoot) {
      const found = el.shadowRoot.querySelector<HTMLElement>(selector);
      if (found) return found;
    }
  }

  return null;
}

/** Describes how the PDF window should be displayed. */
type PdfPlacement =
  | { mode: "floating"; position: { x: number; y: number } }
  | { mode: "takeover" };

/**
 * Resolves the best placement for the PDF window relative to the chat widget.
 *
 * Tries to position the PDF as a floating window beside the chat widget.
 * If the viewport is too small to fit both without overlap, returns takeover mode
 * which renders the PDF over the chat widget instead.
 */
function resolveValidPdfPlacement({
  pdfWidth = PDF_DEFAULT_WIDTH,
  pdfHeight = PDF_DEFAULT_HEIGHT,
}: {
  pdfWidth?: number;
  pdfHeight?: number;
} = {}): PdfPlacement {
  const chatFrame = findChatFrame();
  if (!chatFrame) {
    return {
      mode: "floating",
      position: {
        x: window.innerWidth - pdfWidth - VIEWPORT_MARGIN,
        y: VIEWPORT_MARGIN,
      },
    };
  }

  const rect = chatFrame.getBoundingClientRect();
  const spaceLeft = rect.left;
  const spaceRight = window.innerWidth - rect.right;
  const bottomY = window.innerHeight - rect.bottom;
  const maxBottomY = window.innerHeight - pdfHeight - VIEWPORT_MARGIN;
  const clampedY = Math.max(VIEWPORT_MARGIN, Math.min(maxBottomY, bottomY));
  const requiredWidth = pdfWidth + VIEWPORT_MARGIN + GAP;

  // Try left side
  if (spaceLeft >= requiredWidth) {
    return {
      mode: "floating",
      position: {
        x: rect.left - pdfWidth - GAP,
        y: clampedY,
      },
    };
  }

  // Try right side
  if (spaceRight >= requiredWidth) {
    return {
      mode: "floating",
      position: {
        x: rect.right + GAP,
        y: clampedY,
      },
    };
  }

  // Not enough space on either side — takeover mode
  return { mode: "takeover" };
}

/** Describes how the PDF window should be displayed, with optional dimension overrides. */
type BestFitPlacement =
  | { mode: "floating"; position: { x: number; y: number }; width: number; height: number }
  | { mode: "takeover" };

/**
 * Resolves the best placement for the PDF by trying the requested size first,
 * then progressively reducing width until it fits beside the chat widget.
 * Falls back to takeover mode only when even the minimum width cannot fit.
 */
function resolveBestFitPlacement({
  pdfWidth,
  pdfHeight,
  minWidth = PDF_DEFAULT_WIDTH,
}: {
  pdfWidth: number;
  pdfHeight: number;
  minWidth?: number;
}): BestFitPlacement {
  // Try full requested size first
  const fullFit = resolveValidPdfPlacement({ pdfWidth, pdfHeight });
  if (fullFit.mode === "floating") {
    return { ...fullFit, width: pdfWidth, height: pdfHeight };
  }

  // Full size doesn't fit — calculate the largest width that fits
  const chatFrame = findChatFrame();
  if (!chatFrame) {
    return {
      mode: "floating",
      position: { x: VIEWPORT_MARGIN, y: VIEWPORT_MARGIN },
      width: pdfWidth,
      height: pdfHeight,
    };
  }

  const rect = chatFrame.getBoundingClientRect();
  const spaceLeft = rect.left;
  const spaceRight = window.innerWidth - rect.right;
  const bestSpace = Math.max(spaceLeft, spaceRight);
  const maxWidth = bestSpace - VIEWPORT_MARGIN - GAP;

  if (maxWidth < minWidth) {
    return { mode: "takeover" };
  }

  // Try fitting with the reduced width
  const fitted = resolveValidPdfPlacement({ pdfWidth: maxWidth, pdfHeight });
  if (fitted.mode === "floating") {
    return { ...fitted, width: maxWidth, height: pdfHeight };
  }

  return { mode: "takeover" };
}

// ---------------------------------------------------------------------------
// Inline overlay content & placement helpers
// ---------------------------------------------------------------------------

/** Fullscreen dimensions used when the PDF viewer is maximized. */
const PDF_FULLSCREEN_WIDTH = 1200;
const PDF_FULLSCREEN_HEIGHT = 700;

/**
 * Computes the best initial placement for the PDF window, considering
 * stored dimensions from localStorage that may differ from defaults.
 * Updates storage when best-fit adjusts the dimensions.
 */
function computeInitialPlacement({
  dimensionsStorage,
}: {
  dimensionsStorage: DimensionsStorage;
}): PdfPlacement {
  const storedDims = dimensionsStorage.get();
  const actualWidth = storedDims?.width ?? PDF_DEFAULT_WIDTH;
  const actualHeight = storedDims?.height ?? PDF_DEFAULT_HEIGHT;

  const bestFit = resolveBestFitPlacement({ pdfWidth: actualWidth, pdfHeight: actualHeight });

  if (bestFit.mode === "takeover") return { mode: "takeover" };

  // If best-fit adjusted the dimensions, update storage before WidgetWindow reads it
  if (bestFit.width !== actualWidth || bestFit.height !== actualHeight) {
    dimensionsStorage.save({ width: bestFit.width, height: bestFit.height });
  }

  return { mode: "floating", position: bestFit.position };
}

// ---------------------------------------------------------------------------
// PdfInlineOverlayContent
// ---------------------------------------------------------------------------

/**
 * Content rendered inside the widget inline overlay for takeover mode.
 *
 * Provides a header with back button, title and download, plus the
 * PdfBody for PDF rendering — all within the widget frame boundaries.
 */
function PdfInlineOverlayContent({
  blobUrl,
  isLoading,
  error,
  title,
  onClose,
  initialPageNumber,
}: {
  blobUrl: string | null;
  isLoading: boolean;
  error: string | null;
  title: string;
  onClose: () => void;
  initialPageNumber?: number;
}) {
  const dragHandle = useDragHandle();

  return (
    <div
      className="w-full h-full bg-background rounded-lg overflow-hidden flex flex-col"
      data-testid="pdf-inline-overlay-content"
    >
      <div
        ref={dragHandle?.ref}
        {...dragHandle?.listeners}
        className="flex items-center gap-2 px-4 py-3 border-b cursor-grab active:cursor-grabbing"
      >
        <Button
          size="icon"
          variant="ghost"
          data-testid="pdf-takeover-back"
          aria-label="Back to chat"
          onClick={onClose}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="flex-1 font-medium text-sm truncate">{title}</span>
        <DownloadButton blobUrl={blobUrl} title={title} />
      </div>
      <PdfBody blobUrl={blobUrl} isLoading={isLoading} error={error} initialPageNumber={initialPageNumber} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PdfWindow
// ---------------------------------------------------------------------------

/**
 * Self-contained PDF window that composes WidgetWindow with PDF-specific
 * header controls (download, fullscreen, close) and a body area that
 * renders the PDF using react-pdf with continuous scroll and zoom controls.
 *
 * Automatically chooses between floating window mode and takeover panel
 * mode based on available viewport space. When the viewport is too small
 * to place the PDF beside the chat widget, it slides over the chat instead.
 */
function PdfWindow({
  staticUrl,
  title,
  isVisible,
  onClose,
  pageNumber,
}: {
  staticUrl: string;
  title: string;
  isVisible: boolean;
  onClose: () => void;
  pageNumber?: number;
}) {
  const { blobUrl, isLoading, error } = useFetchPdfBlob({ staticUrl });
  const { forceTopLayer } = usePersonation();
  const overlay = useWidgetOverlayOptional();
  const overlayShow = overlay?.showOverlay;
  const overlayHide = overlay?.hideOverlay;

  const pdfDimensionsStorage = React.useMemo(
    () => createDimensionsStorage({ key: "pdf_dimensions" }),
    [],
  );

  const [placement, setPlacement] = React.useState<PdfPlacement>(() =>
    computeInitialPlacement({ dimensionsStorage: pdfDimensionsStorage }),
  );

  // Recalculate placement when staticUrl changes (new PDF opened)
  React.useEffect(() => {
    setPlacement(computeInitialPlacement({ dimensionsStorage: pdfDimensionsStorage }));
  }, [staticUrl, pdfDimensionsStorage]);

  // Recalculate placement when viewport is resized
  React.useEffect(() => {
    let rafId: number | null = null;
    const handleResize = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setPlacement(computeInitialPlacement({ dimensionsStorage: pdfDimensionsStorage }));
        rafId = null;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [pdfDimensionsStorage]);

  /**
   * Resolves position for the WidgetWindow when fullscreen is toggled.
   * When entering fullscreen, recalculates with fullscreen dimensions.
   * When exiting, recalculates with default dimensions.
   * If the result is takeover, transitions the placement state and returns null
   * so WidgetWindow uses its default (which won't matter since it will unmount).
   */
  const resolveTogglePosition = React.useCallback(
    ({ entering, width, height }: { entering: boolean; width: number; height: number }) => {
      if (entering) {
        const bestFit = resolveBestFitPlacement({ pdfWidth: width, pdfHeight: height });
        if (bestFit.mode === "takeover") {
          setTimeout(() => setPlacement({ mode: "takeover" }), 0);
          return null;
        }
        return { ...bestFit.position, width: bestFit.width, height: bestFit.height };
      }

      // Exiting fullscreen — recalculate with default dimensions
      const newPlacement = resolveValidPdfPlacement();
      if (newPlacement.mode === "takeover") {
        setTimeout(() => setPlacement({ mode: "takeover" }), 0);
        return null;
      }
      return newPlacement.position;
    },
    [],
  );

  // Update overlay content when PDF props change (without triggering hide/show cycle)
  React.useEffect(() => {
    if (placement.mode !== "takeover" || !overlayShow || !overlayHide) return;

    if (isVisible) {
      overlayShow({
        content: (
          <PdfInlineOverlayContent
            blobUrl={blobUrl}
            isLoading={isLoading}
            error={error}
            title={title}
            onClose={onClose}
            initialPageNumber={pageNumber}
          />
        ),
      });
    } else {
      overlayHide();
    }
  }, [placement.mode, isVisible, overlayShow, overlayHide, blobUrl, isLoading, error, title, onClose, pageNumber]);

  // Cleanup: hide overlay only on unmount or when leaving takeover mode
  React.useEffect(() => {
    if (placement.mode !== "takeover" || !overlayHide) return;
    return () => {
      overlayHide();
    };
  }, [placement.mode, overlayHide]);

  if (placement.mode === "takeover") {
    return null;
  }

  return (
    <WidgetWindow.Provider
      storageKeyPrefix="pdf"
      defaultWidth={PDF_DEFAULT_WIDTH}
      defaultHeight={PDF_DEFAULT_HEIGHT}
      fullscreenWidth={PDF_FULLSCREEN_WIDTH}
      fullscreenHeight={PDF_FULLSCREEN_HEIGHT}
      isVisible={isVisible}
      onClose={onClose}
      dndId="pdf-viewer-frame"
      onClickOutside={onClose}
      initialPosition={placement.position}
      resolveTogglePosition={resolveTogglePosition}
      overlayZIndex={forceTopLayer ? TOP_LAYER_Z.PDF_OVERLAY : undefined}
      frameZIndex={forceTopLayer ? TOP_LAYER_Z.PDF_FRAME : undefined}
    >
      <WidgetWindow.Header>
        <span className="flex-1 font-medium text-sm truncate">{title}</span>
        <DownloadButton blobUrl={blobUrl} title={title} />
        <WidgetWindow.FullScreenToggle />
        <WidgetWindow.CloseButton />
      </WidgetWindow.Header>
      <PdfBody blobUrl={blobUrl} isLoading={isLoading} error={error} initialPageNumber={pageNumber} />
    </WidgetWindow.Provider>
  );
}

// ---------------------------------------------------------------------------
// PdfViewer (main export)
// ---------------------------------------------------------------------------

/**
 * Top-level PDF viewer component.
 *
 * Reads the current PDF state from PdfContext. Renders nothing when no
 * PDF is open. When a PDF is active, renders a PdfWindow with the PDF
 * content and window controls.
 *
 * Must be placed inside a PdfViewerProvider.
 */
export function PdfViewer() {
  const { currentPdf, isVisible, closePdf } = usePdfViewer();

  if (!currentPdf) {
    return null;
  }

  return (
    <PdfWindow
      staticUrl={currentPdf.staticUrl}
      title={currentPdf.title}
      isVisible={isVisible}
      onClose={closePdf}
      pageNumber={currentPdf.pageNumber}
    />
  );
}

PdfViewer.displayName = "PdfViewer";
